import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import type { Editor } from '@tiptap/react';
import { Rocket } from 'lucide-react';
import React, { useRef, useState } from 'react';
import { toast } from 'sonner';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/Dialog';
import RichTextField from '@/components/RichTextField';
import { Badge } from '@/components/ui/badge';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { useOdooAuth } from '@/contexts/OdooAuthContext';

const LAUNCH_FINAL_IC_VOTE = gql`
  mutation LaunchFinalIcVote(
    $dealId: ID!
    $templateId: ID!
    $selectedFundIds: [ID!]!
    $body: String
    $title: String
    $note: String
  ) {
    launchFinalIcVote(
      dealId: $dealId
      templateId: $templateId
      selectedFundIds: $selectedFundIds
      body: $body
      title: $title
      note: $note
    ) {
      ok
      message
      voteCount
    }
  }
`;

interface LaunchFinalIcVoteData {
  launchFinalIcVote: {
    message: null | string;
    ok: boolean;
    voteCount: number;
  };
}

interface LaunchFinalICVoteDialogProps {
  children?: React.ReactNode;
  committees?: ParticipatingCommittee[];
  dealId?: string;
  disabled?: boolean;
  onCommitteeCreated?: () => void;
}

interface LaunchFinalIcVoteVariables {
  body?: string;
  dealId: string;
  note?: string;
  selectedFundIds: string[];
  templateId: string;
  title?: string;
}

interface ParticipatingCommittee {
  description: null | string;
  fundId: {
    id: string;
    name: string;
  } | null;
  id: string;
  isActive: boolean;
  memberships: {
    endDate: null | string;
    id: string;
    role: string;
    startDate: null | string;
    userId: {
      id: string;
      name: string;
    } | null;
  }[];
  name: string;
  stageMapping: null | string;
  threshold: number;
  votingOptions: null | string;
}

const GET_IC_VOTING_EMAIL_TEMPLATES = gql`
  query GetIcVotingEmailTemplates {
    mailTemplates(templateType: "ic_voting") {
      edges {
        node {
          id
          name
          subject
          bodyHtml
          emailFrom
          emailTo
          templateType
          model
          modelId {
            id
            model
            name
          }
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`;

interface GetIcVotingEmailTemplatesData {
  mailTemplates: {
    edges: {
      node: MailTemplate;
    }[];
    pageInfo: {
      endCursor: null | string;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
      startCursor: null | string;
    };
  };
}

interface MailTemplate {
  bodyHtml: null | string;
  emailFrom: null | string;
  emailTo: null | string;
  id: string;
  model: null | string;
  modelId: {
    id: string;
    model: string;
    name: string;
  } | null;
  name: string;
  subject: null | string;
  templateType: null | string;
}

const Step1 = ({
  committees,
  onToggleCommittee,
  selectedIds
}: {
  committees: ParticipatingCommittee[];
  onToggleCommittee: (id: string) => void;
  selectedIds: Set<string>;
}) => {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-black">
          Committee & Voter Review
        </h3>
      </div>

      <div className="border-y">
        <Table>
          <TableHeader>
            <TableRow className="border-none hover:bg-transparent">
              <TableHead className="w-[40px] px-2"></TableHead>
              <TableHead className="text-sm font-semibold text-black">
                Committee Name
              </TableHead>
              <TableHead className="text-sm font-semibold text-black">
                Fund
              </TableHead>
              <TableHead className="text-sm font-semibold text-black">
                Treshold
              </TableHead>
              <TableHead className="text-sm font-semibold text-black">
                Member Count
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {committees.map(c => (
              <TableRow
                className="border-none hover:bg-transparent"
                key={c.id}
              >
                <TableCell className="px-2">
                  <Checkbox
                    checked={selectedIds.has(c.id)}
                    className="data-[state=checked]:border-[#0b0f19] data-[state=checked]:bg-[#0b0f19]"
                    onCheckedChange={() => {
                      onToggleCommittee(c.id);
                    }}
                  />
                </TableCell>
                <TableCell className="text-sm text-black">{c.name}</TableCell>
                <TableCell className="text-sm text-black">
                  {c.fundId?.name ?? 'N/A'}
                </TableCell>
                <TableCell className="text-sm text-black"></TableCell>
                <TableCell className="text-sm text-black">
                  <div className="flex items-center justify-between gap-4">
                    <span>{c.threshold} Required</span>
                    <div className="flex -space-x-1.5 overflow-hidden">
                      {c.memberships.slice(0, 3).map(m => (
                        <div
                          className="inline-flex size-6 items-center justify-center rounded-full border-2 border-white bg-slate-100 text-[8px] font-bold text-slate-600 ring-white"
                          key={m.id}
                        >
                          {m.userId?.name
                            ? m.userId.name
                                .split(' ')
                                .map(n => n[0])
                                .join('')
                            : '??'}
                        </div>
                      ))}
                      {c.memberships.length > 3 && (
                        <div className="inline-flex size-6 items-center justify-center rounded-full border-2 border-white bg-slate-200 text-[8px] font-bold text-slate-800 ring-white">
                          +{c.memberships.length - 3}
                        </div>
                      )}
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="space-y-4 pt-2">
        <h3 className="text-lg font-semibold text-black">Summary Panel</h3>
        <Separator />
        <div className="flex py-2">
          <span className="w-[300px] text-sm font-medium text-black">
            Total Unique Voters
          </span>
          <span className="text-muted-foreground text-sm">
            {
              Array.from(
                new Set(
                  committees
                    .filter(c => selectedIds.has(c.id))
                    .flatMap(c => c.memberships.map(m => m.userId?.name))
                )
              ).length
            }
          </span>
        </div>
        <p className="text-muted-foreground text-sm">
          Each person votes once. Their vote is counted toward every fund
          committee they belong to.
        </p>
      </div>
    </div>
  );
};

type EmailMode = 'custom' | 'template';

const Step2 = ({
  bodyEditorRef,
  emailMode,
  loading: templatesLoading,
  note,
  onEmailModeChange,
  onnoteChange,
  onSelectedTemplateIdChange,
  ontitleChange,
  selectedTemplateId,
  templates,
  title
}: {
  bodyEditorRef: React.RefObject<Editor | null>;
  emailMode: EmailMode;
  loading: boolean;
  note: string;
  onEmailModeChange: (mode: EmailMode) => void;
  onnoteChange: (note: string) => void;
  onSelectedTemplateIdChange: (id: null | string) => void;
  ontitleChange: (title: string) => void;
  selectedTemplateId: null | string;
  templates: MailTemplate[];
  title: string;
}) => {
  React.useEffect(() => {
    if (templates.length > 0 && !selectedTemplateId) {
      onSelectedTemplateIdChange(templates[0].id);
    }
  }, [templates, selectedTemplateId, onSelectedTemplateIdChange]);

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-black">Email Preview</h3>
        <RadioGroup
          className="flex gap-6"
          onValueChange={v => {
            onEmailModeChange(v as EmailMode);
          }}
          value={emailMode}
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem
              className="data-[state=checked]:border-[#0b0f19] data-[state=checked]:bg-[#0b0f19]"
              id="email-mode-custom"
              value="custom"
            />
            <Label
              className="cursor-pointer font-normal"
              htmlFor="email-mode-custom"
            >
              Create your own email
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem
              className="data-[state=checked]:border-[#0b0f19] data-[state=checked]:bg-[#0b0f19]"
              id="email-mode-template"
              value="template"
            />
            <Label
              className="cursor-pointer font-normal"
              htmlFor="email-mode-template"
            >
              Use Email Template
            </Label>
          </div>
        </RadioGroup>

        {emailMode === 'custom' && (
          <div className="space-y-4">
            <div className="flex w-full gap-3">
              <div className="w-1/2 space-y-2">
                <Field>
                  <FieldLabel htmlFor="email-title-preview">
                    Email Title Preview
                  </FieldLabel>
                  <Input
                    id="email-title-preview"
                    onChange={e => {
                      ontitleChange(e.target.value);
                    }}
                    placeholder="Add your summary here"
                    value={title}
                  />
                </Field>
              </div>
              <div className="w-1/2 space-y-2">
                <Field>
                  <FieldLabel htmlFor="email-note-preview">
                    Email Note Preview
                  </FieldLabel>
                  <Input
                    id="email-note-preview"
                    onChange={e => {
                      onnoteChange(e.target.value);
                    }}
                    placeholder="Add your summary here"
                    value={note}
                  />
                </Field>
              </div>
            </div>
            <div className="space-y-2">
              <Field>
                <FieldLabel>Email Body Preview</FieldLabel>
                <RichTextField
                  defaultValue=""
                  dropdownValues={[
                    {
                      label: 'Funds',
                      value: '__FUNDS__'
                    },
                    {
                      label: 'Member Name',
                      value: '__MEMBER_NAME__'
                    },
                    {
                      label: 'Deal Name',
                      value: '__DEAL_NAME__'
                    }
                  ]}
                  ref={bodyEditorRef}
                />
              </Field>
            </div>
          </div>
        )}
      </div>

      <Separator />

      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-black">
          Email Template Selector
        </h3>
        {templatesLoading ? (
          <div className="text-muted-foreground flex min-h-[120px] items-center justify-center text-sm">
            Loading templates…
          </div>
        ) : templates.length === 0 ? (
          <div className="text-muted-foreground flex min-h-[120px] items-center justify-center text-sm">
            No email templates available
          </div>
        ) : (
          <RadioGroup
            className="grid gap-4 sm:grid-cols-2"
            onValueChange={id => {
              onSelectedTemplateIdChange(id || null);
            }}
            value={selectedTemplateId ?? ''}
          >
            {templates.map(template => (
              <div
                className="flex cursor-pointer flex-col overflow-hidden rounded-lg border transition-colors hover:border-[#0b0f19]/40 data-[state=checked]:border-[#0b0f19]"
                key={template.id}
                onClick={() => {
                  onSelectedTemplateIdChange(template.id);
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelectedTemplateIdChange(template.id);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <div className="flex items-center gap-2 border-b p-3">
                  <RadioGroupItem
                    className="data-[state=checked]:border-[#0b0f19] data-[state=checked]:bg-[#0b0f19]"
                    id={`template-${template.id}`}
                    value={template.id}
                  />
                  <Label
                    className="cursor-pointer font-medium"
                    htmlFor={`template-${template.id}`}
                  >
                    {template.name}
                  </Label>
                </div>
                <div className="bg-muted/30 min-h-[350px] overflow-hidden text-sm">
                  {template.bodyHtml ? (
                    <div>
                      <div
                        className="origin-top-left"
                        style={{
                          height: '350px',
                          transform: 'scale(0.5)',
                          transformOrigin: 'top left',
                          width: '200%'
                        }}
                      >
                        <iframe
                          className="h-[700px] w-full border-0 bg-white"
                          sandbox="allow-same-origin"
                          srcDoc={template.bodyHtml}
                          title={`Preview of ${template.name}`}
                        />
                      </div>
                    </div>
                  ) : (
                    <p className="text-muted-foreground mt-2 italic">
                      No body content
                    </p>
                  )}
                </div>
              </div>
            ))}
          </RadioGroup>
        )}
      </div>
    </div>
  );
};

export const LaunchFinalICVoteDialog = ({
  children,
  committees = [],
  dealId,
  disabled,
  onCommitteeCreated
}: LaunchFinalICVoteDialogProps) => {
  const { role } = useOdooAuth();
  const isAdmin = role === 'admin';
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [selectedTemplateId, setSelectedTemplateId] = useState<null | string>(
    null
  );
  const [emailMode, setEmailMode] = useState<EmailMode>('custom');
  const [title, settitle] = useState('');
  const [note, setnote] = useState('');
  const bodyEditorRef = useRef<Editor | null>(null);

  const { data: templatesData, loading: templatesLoading } =
    useQuery<GetIcVotingEmailTemplatesData>(GET_IC_VOTING_EMAIL_TEMPLATES, {
      skip: !open
    });
  const templates =
    templatesData?.mailTemplates.edges.map(edge => edge.node) ?? [];

  const [launchFinalIcVote, { loading: isLaunching }] = useMutation<
    LaunchFinalIcVoteData,
    LaunchFinalIcVoteVariables
  >(LAUNCH_FINAL_IC_VOTE);

  React.useEffect(() => {
    if (committees.length > 0 && selectedIds.size === 0) {
      const activeIds = committees.filter(c => c.isActive).map(c => c.id);
      setSelectedIds(new Set(activeIds));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [committees]);

  const resetForm = () => {
    setStep(1);
    setSelectedTemplateId(null);
    setEmailMode('custom');
    settitle('');
    setnote('');
    const activeIds = committees.filter(c => c.isActive).map(c => c.id);
    setSelectedIds(new Set(activeIds));
  };

  // Extract email body HTML with token values replaced
  const getEmailBodyWithTokens = (): string => {
    const editor = bodyEditorRef.current;
    if (!editor) return '';

    const container = document.createElement('div');
    container.innerHTML = editor.getHTML();

    container.querySelectorAll('span[data-value]').forEach(span => {
      const value = (span as HTMLElement).dataset.value ?? '';
      span.replaceWith(value);
    });

    return container.innerHTML;
  };

  const handleToggleCommittee = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleNext = async () => {
    if (step < 2) {
      setStep(prev => (prev + 1) as 1 | 2 | 3);
      return;
    }

    if (!dealId || !selectedTemplateId) {
      toast.error(
        'Deal and email template are required to send voting emails.'
      );
      return;
    }

    const selectedFundIds = Array.from(
      new Set(
        committees
          .filter(c => selectedIds.has(c.id))
          .flatMap(c => (c.fundId?.id ? [c.fundId.id] : []))
      )
    );

    if (selectedFundIds.length === 0) {
      toast.error(
        'Select at least one committee with a fund to send voting emails.'
      );
      return;
    }

    try {
      const body =
        emailMode === 'custom' ? getEmailBodyWithTokens() : undefined;

      const result = await launchFinalIcVote({
        variables: {
          body,
          dealId,
          note: emailMode === 'custom' ? note : undefined,
          selectedFundIds,
          templateId: selectedTemplateId,
          title: emailMode === 'custom' ? title : undefined
        }
      });

      const data = result.data?.launchFinalIcVote;
      if (data?.ok) {
        toast.success(
          data.message ??
            `Voting emails sent (${String(data.voteCount)} vote(s)).`
        );
        setOpen(false);
        resetForm();
        onCommitteeCreated?.();
      } else {
        toast.error(data?.message ?? 'Failed to send voting emails.');
      }
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to send voting emails.'
      );
    }
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <>
      <Dialog
        onOpenChange={newOpen => {
          setOpen(newOpen);
          if (!newOpen) {
            resetForm();
          }
        }}
        open={open}
      >
        <DialogTrigger asChild>
          {children ?? (
            <Button
              className="bg-[#003455] text-white shadow-sm hover:bg-[#002a44]"
              disabled={disabled}
              size="lg"
            >
              <Rocket className="mr-2 h-5 w-5" />
              Launch Final IC Voting
            </Button>
          )}
        </DialogTrigger>
        <DialogContent
          aria-describedby={undefined}
          className="flex max-h-[90vh] flex-col sm:max-w-4xl"
        >
          <DialogHeader className="flex-shrink-0">
            <div className="flex items-center gap-2">
              <DialogTitle className="text-xl font-semibold">
                Launch Final IC Vote
              </DialogTitle>
              <Badge className="border-transparent bg-[#FEF9C3] px-2 py-0.5 text-xs font-medium text-[#854D0E] hover:bg-[#FEF9C3]">
                Unsaved
              </Badge>
            </div>
          </DialogHeader>

          <DialogBody>
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
              <Breadcrumb className="bg-background sticky top-0 z-10 flex-shrink-0 border-b py-4">
                <BreadcrumbList>
                  {[1, 2].map(s => (
                    <React.Fragment key={s}>
                      <BreadcrumbItem>
                        {step === s ? (
                          <BreadcrumbPage className="font-bold text-black">
                            Step {s}
                          </BreadcrumbPage>
                        ) : (
                          <button
                            className="text-muted-foreground text-sm transition-colors hover:text-black"
                            onClick={() => {
                              setStep(s as 1 | 2 | 3);
                            }}
                            type="button"
                          >
                            Step {s}
                          </button>
                        )}
                      </BreadcrumbItem>
                      {s < 2 && (
                        <BreadcrumbSeparator className="[&>svg]:size-3" />
                      )}
                    </React.Fragment>
                  ))}
                </BreadcrumbList>
              </Breadcrumb>

              <div className="space-y-6 py-4">
                {step === 1 && (
                  <Step1
                    committees={committees}
                    onToggleCommittee={handleToggleCommittee}
                    selectedIds={selectedIds}
                  />
                )}
                {step === 2 && (
                  <Step2
                    bodyEditorRef={bodyEditorRef}
                    emailMode={emailMode}
                    loading={templatesLoading}
                    note={note}
                    onEmailModeChange={setEmailMode}
                    onnoteChange={setnote}
                    onSelectedTemplateIdChange={setSelectedTemplateId}
                    ontitleChange={settitle}
                    selectedTemplateId={selectedTemplateId}
                    templates={templates}
                    title={title}
                  />
                )}
              </div>
            </div>
          </DialogBody>

          <DialogFooter className="flex-shrink-0">
            <Button
              className="bg-[#003455] text-white hover:bg-[#002a44]"
              disabled={step === 2 && isLaunching}
              onClick={() => void handleNext()}
            >
              {step === 2 && isLaunching
                ? 'Sending…'
                : step === 2
                  ? 'Send Voting Emails'
                  : 'Next'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
